export const gerçeklik = `
# Zihin, Gerçekliği Nasıl İnşa Eder?

Bu yazıda, insan zihninin dış dünyayı nasıl tahmin ettiğini, bu tahminlerin nasıl duygulara, eylemlere ve deneyimlere dönüştüğünü; zihnin kendini nasıl modelleyip yeniden şekillendirdiğini farklı kuramsal yaklaşımlar eşliğinde ele alıyorum.
## Algı Gerçeklikten Mi Beslenir, Zihinden Mi?

> **“Beynimizin amacı geleceği tahmin ederek hayatta kalmaktır.”**

Öncelikle, insan zihninin gerçeklikle olan ilişkisini anlamak için Karl Friston’un _Free-Energy_ ilkesine yakından bakacağım.

Beyin, dış dünyadan gelen duyusal bilgileri pasifçe almaz.
Aksine, **önceden oluşturduğu tahminlerle** gelen bilgiyi yorumlar.

— _“Beyin bir tahmin makinesidir.”_

Friston’ın _free-energy_ modeline göre beyin, **boş bir sayfa (tabula rasa)** değildir.
Beyin, **genetik olarak önceden kabaca yapılandırılmış** bazı tahmin mekanizmalarıyla doğar.

Yani bebek her şeyi bilerek doğmaz, ama **neleri öğrenmenin hayatta kalma açısından anlamlı olduğuna** dair **önsel eğilimlerle** gelir.

> _Tıpkı gözün ışık algılamaya, kulağın ses frekanslarına duyarlı şekilde doğması gibi…_

Yeni doğan bebek gözüyle bakar ama gördüğü şeyin ne olduğunu henüz anlamaz, ses duyar ama bu sesin kimden geldiğini bilmez.

Beyin burada **istatistiksel örüntüler** toplamaya başlar. Her gelen veriye bir tahmin eşlik eder:

> “Bu şey bir öncekiyle aynı mı?”
> “Bununla sonra ne oluyor?”

İşte bu süreçle birlikte; içsel modeller oluşur. Her yeni deneyim, eski tahmini test eder, sürpriz varsa model güncellenir. Sürpriz tahmin hatasıdır.

O zaman şöyle diyebiliriz:

Beynimiz dış dünyayı doğuştan anlamaz, ama **anlamaya hazır, tahmin kurmaya meyilli bir mimariyle doğar.**

Bu tahminler geçmiş deneyimlere, öğrenilmiş kalıplara ve içsel modellerimize dayanır.

Bunlar zihinsel “dünyamızdır.”

**Robert Greene’in “The Laws of Human Nature”** kitabına göre deneyimler, öğrenilmiş kalıplar ve beklentiler şu şekilde oluşur:

Çocuklukta yaşanan sosyal ilişkiler ve otorite figürlerinden gelen tepkiler, kişinin neyin doğru ya da tehlikeli olduğunu öğrenmesini sağlar ve zamanla bu öğrenilen tepkiler otomatik hale gelir.

Öğrenilmiş kalıplar, geçmişte öğrendikleri kalıplarla düşünmeden tepki verir ve kararlarını bu otomatik davranışlarla yönlendirir.

Beklentiler ise, geçmişte yaşanan olumlu ya da olumsuz deneyimlerin izleriyle oluşur. Greene’e göre, bir kişi bir durumda ödül veya kabul gördüyse, benzer bir durumda tekrar aynı sonucu alacağına dair bir beklenti geliştirir. Bu beklentiler zamanla kişiliğin ve davranış repertuvarının bir parçası olur.

İşte burada şu soruyu sorarız:
**“Kurduğumuz bu içsel tahmin modeli, dış gerçeklikle ne kadar uyumlu?”**

İşte burada **özgür enerji ilkesi (free-energy principle)** devreye giriyor.

Karl Friston’a göre:

> **Özgür enerji**, beynin yaptığı tahminlerle duyulardan gelen gerçek veriler arasındaki farkın bir ölçüsüdür.

Bu fark ne kadar büyükse, beynin “sürprizle” karşılaşma olasılığı o kadar yüksektir.

Ve sürpriz, enerji kaybı demektir. Hayatta kalma riski demektir.
Bu yüzden beyin, **sürprizi minimize etmek zorundadır.**
Tıpkı denge arayan bir sistem gibi, _kendini sürekli olarak gerçekliğe ayarlamak zorundadır._

Friston’un modeli, beynin sadece “tahmin yapan” değil, **tahminini gerçekleştirmeye çalışan bir sistem** olduğunu da söyler.

Yani:

Beyin bir **içsel model** kurar: Bir yolda yürüyorsun. Zemin hep düzdü, adımların hep aynı ritimde ilerledi.
Zihnin şöyle der: “Bir sonraki adımda da sert bir zemin olacak.”

Her adımda modelin tahmin doğruluğu onaylanır.

Ama bu kez, önceden fark etmediğin küçük bir çukur var. Ayağını yere indiriyorsun — ve bir anda boşluğa denk geliyorsun.
“Beklediğim zemin burada değildi!”

Bu küçük sürpriz, beynin içsel modeline şu mesajı verir:
“Bu yol eskisi gibi değil. Yeni bir şey var. Güncelle.”

Beyin bir sonraki adımda artık daha temkinlidir.
Model değişmiştir. Beklenti değişmiştir.
Çukurun fark edilmesiyle birlikte **sürpriz azalır**, uyum artar, enerji korunur.

Bu sürece **Aktif Çıkarım (Active Inference)** denir.
Beyin yalnızca anlamaz, _anlamını test eder._

## Peki Ya Düşüncelerimizin Farkında Olmak?

Algı, duyular ve tahminlerden meydana geliyor, _peki ya tahminin kendisinin farkında olmak?_

İşte bu noktada devreye giren kavram: **Metakognisyon.**
Yani: **“Kendi zihinsel süreçlerinin farkında olmak.”**
Sadece bir şeyi **düşünmekle** kalmazsın,
aynı zamanda **nasıl düşündüğünü, neden öyle düşündüğünü, doğru mu düşündüğünü** de fark eder ve sorgularsın.

> “Beyin aynı zamanda kurduğu modeli gözden geçirir,
> kendi iç dünyasında yeniden modeller.”

Birey, neyi bildiğinin, nasıl düşündüğünün ve hangi stratejileri kullandığının farkındadır. Kişi, kendi düşünme süreçlerini eleştirel biçimde değerlendirir. Bu farkındalık ve değerlendirme sonucunda kişi, kendi düşünme süreçlerine yön verir. Plan yapar, strateji değiştirir, hataları düzeltir ve hedeflerine ulaşmak için düşünce akışını yeniden yapılandırır.

Sadece _“Ben böyle düşünüyorum”_ demez;
aynı zamanda _“O ne düşünüyor?”, “Nasıl hissediyor?”,_
hatta _“Benim hakkımda ne düşünüyor olabilir?”_ gibi çok katmanlı çıkarımlar yapar.

Bu yeteneğe psikolojide **zihin kuramı (Theory of Mind)** denir.
Empatiden stratejik düşünmeye, hikâye anlatımından sosyal iş birliğine kadar pek çok insani becerinin temeli, bu zihinsel modelleme kapasitesine dayanır.

## Zamanın ötesinde düşünmek

İnsan zihni, yalnızca içinde bulunduğu ânı algılamakla kalmaz;
**geçmişe döner, geleceği kurar, hiç yaşanmamış olasılıkları zihninde canlandırır.**

Bu yetenek yalnızca bilgi edinmek için değil, aynı zamanda **kendini ve dünyayı modellemek, anlamlandırmak ve yön bulmak** içindir.

Hayal kurmak, sadece gelecek için değil, **kendini tanımlamak** için de gereklidir.

> “Nasıl bir insan olmak istiyorum?”
> “Geçmişte kimdim, şimdi kimim?”

Bu tür hayaller, kimliğimizin zihinsel çekirdeğini oluşturur.

## Zihin, karmaşıklık karşısında üst bir modele ihtiyaç duyar.

Bilgi çoğaldıkça belirsizlik artar, yanıltıcı yollar çoğalır, “Doğru bilgi nedir?” sorusu baskınlaşır. Burada zihin çoğu zaman ya **içsel bir bilge ses** kurar (vicdan, sezgi, üst-benlik) ya da **dışsal bir rehbere** yönelir:

> **İnsan zihni, bilgi arayışında yalnızca kendi aklına değil, daha yüksek bir akıl — bir otorite, bir bilge arar.**

Bu konuda çeşitli teoriler ortaya atılmıştır. Bunlardan en kapsamlı olanlarından biri, filozof **Linda Trinkaus Zagzebski** tarafından geliştirilen **Epistemic Authority Theory**’dir.

Bu teoriye göre, bir birey **kendi düşünsel süreçlerine vicdanlı ve bilinçli bir şekilde güveniyorsa**, benzer biçimde düşünsel olarak sorumlu ve güvenilir olan bir başkasına da güvenmesi rasyoneldir.
Zagzebski’ye göre bu tür güven, aklın terk edilmesi değil; aksine, **aklın kendi sınırlılıklarını tanıyarak daha kapsayıcı bir bilgi yapısına yönelmesidir.**

Başka bir ifadeyle:

Zihin yalnızca bilgi üretmek istemez —
**“doğru bilgi”ye ulaşmak için güvenilir zihinsel pusulalar** da arar.

Bu pusula bazen:

- Bilimsel bir otorite,
- Bir öğretmen,
- Bir filozof,
- Ya da içselleştirilmiş bir “bilge benlik” olabilir.

## Zihin Duyguyu İnşa Eder

**Duygular, olayları değerlendirme (appraisal) süreciyle ortaya çıkar.**
Bu yaklaşım, bilişsel duygu kuramları arasında en çok kabul gören modellerden biridir ve kökeni psikolog **Richard Lazarus**’a dayanır.

Lazarus’a göre, bir olayın kişi için ne ifade ettiğini belirleyen şey, **olayın kendisi değil**, bireyin onu nasıl **değerlendirdiğidir.**
Bu teori ilk olarak kapsamlı şekilde **“Cognitive-Motivational-Relational Theory of Emotion”** başlığıyla geliştirilmiş, daha sonra pek çok çalışma tarafından genişletilmiştir.

Zihin sadece anlam kurmaz, his de kurar. Düşünce ve modelleme süreci tamamlandıktan sonra, zihin bu modele uygun bir **duygusal çıktı** üretir.

Bu yaklaşımın en güçlü kuramsal açıklamalarından biri, nörobilimci **Lisa Feldman Barrett** tarafından geliştirilen **Constructed Emotion Theory**’dir.

_Duygular doğrudan dış dünyadan değil; zihin tarafından algılanan durumun, geçmiş deneyimlerle ilişkilendirilip mevcut bağlam içinde anlamlandırılmasıyla inşa edilir._

Bu duruma aynı cümlenin _iki farklı kişiyle_ kurulduğu durumu örnekleyerek duygunun nasıl “dışta değil, içeride” kurulduğunu anlatacağım.

**Cümle:**
“Anlatsam da sen bunu anlamazsın.”

**1. Kişi: Ayşe**
— Geçmişte seni sık sık küçümsemiş, fikrini hafife almış biri.

**Geçmiş:** Daha önce düşüncelerimi yok saymıştı.
**Bağlam:** Şu anda önemli bir konuda destek bekliyorum.
**Model:** Yine beni değersizleştiriyor.
**Duygu:** Öfke, kırgınlık, dışlanma hissi

**2. Kişi: Deniz**
— Seni önemseyen, konuları sakin anlatan, iyi niyetli bir arkadaş.

**Geçmiş:** Her zaman açıklayıcı olmaya çalışır ama bazı şeyleri karmaşık anlatır.
**Bağlam:** Teknik bir konudan bahsediyoruz, biraz kafası karışık.
**Model:** “Bu konuyu anlatmak gerçekten karmaşık olabilir” diye düşünüyor.
**Duygu:** Biraz hayal kırıklığı ve sükûnet

Duygu, harekete geçirici bir güçtür. Bu duygu, durumu önem sırasına koyar:

> “Bu önemli, buna tepki vermeliyim.” veya “Bu zararsız, boşver.”

Eylem, zihinsel modele uygun bir davranış planıdır. Eylem sonuç doğurur.
Zihin bu sonucu gözlemler ve **geribildirim alır.**

> Beklentim doğru çıktı mı?
> Sürprizle karşılaştım mı?

Böylece zihin içsel modelini **günceller.** Ve döngü yeniden başlar — ama artık zihin bir önceki döngüden **daha donanımlıdır.**

## Kaynakça

Barrett, L. F. (2017). _How emotions are made: The secret life of the brain._ Houghton Mifflin Harcourt.

Friston, K. (2010). The free-energy principle: A unified brain theory? _Nature Reviews Neuroscience, 11_(2), 127–138. [https://doi.org/10.1038/nrn2787](https://doi.org/10.1038/nrn2787)

Greene, R. (2018). _The laws of human nature._ Viking Press.

Lazarus, R. S. (1991). Emotion and adaptation. In Pervin, L. A. (Ed.), _Handbook of personality: Theory and research_ (pp. 609–637). New York: Oxford University Press.

Moors, A. (2013). Appraisal theory of emotion. In N. M. Seel (Ed.),
_Encyclopedia of the Sciences of Learning_ (pp. 243–245). Springer.
[https://doi.org/10.1007/978-1-4419-1428-6_493](https://doi.org/10.1007/978-1-4419-1428-6_493)

Nichols, S. (2014). _Boundaries of the mind: The individual in the fragile sciences: Cognition._ Oxford University Press.

Zagzebski, L. (2012). _Epistemic authority: A theory of trust, authority, and autonomy in belief._ Oxford University Press.

Veenman, M. V. J. (2013). Metacognition. In N. M. Seel (Ed.),
_Encyclopedia of the Sciences of Learning_ (pp. 2203–2206). Springer.
[https://doi.org/10.1007/978-1-4419-1428-6_493](https://doi.org/10.1007/978-1-4419-1428-6_493)
`;
